import { SelectChangeEvent } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useMemo, useState } from "react";
import Draggable from "react-draggable";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { sendUserMessages } from "~/features/andon/andon.logic";
import { extractDisplayName, extractReceipientId } from "~/lib/helpers";
import { hubs } from "~/lib/signalr";

import { useGetAutostoreGridsQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";
import { SystemMode, WorkstationPortDto } from "~/types/api";

function safeSelectValue<T extends string>(
  value: T,
  options: readonly T[],
  fallback: T = "--" as T
): T {
  return options.includes(value) ? value : fallback;
}

function EventSimulator() {
  const { t } = useTranslation();
  const draggableRef = useRef(null);
  const dispatch = useAppDispatch();

  const siteWorkstationId = useAppSelector(
    (state) => state.workstations.siteWorkstation?.id
  );
  const sitePortId = useAppSelector((state) => state.workstations.sitePortId);
  const siteFulfillmentCenterId = useAppSelector(
    (state) => state.workstations.siteFulfillmentCenterId
  );

  const { data: workstations } = useGetWorkstationsQuery();

  const workstationsMap = useMemo(
    () => Object.fromEntries((workstations ?? []).map((ws) => [ws.id, ws])),
    [workstations]
  );

  const { data: gridList = [] } = useGetAutostoreGridsQuery();

  const methods = useForm({
    defaultValues: {
      selectedPortId: "--",
      selectedWorkstationId: "--",
      selectedGridId: "--",
      receiverUserId: "--",
      selectedBinId: "",
      binWorkstationId: "--"
    }
  });

  const { setValue, watch, getValues } = methods;

  const selectedPortId = watch("selectedPortId");
  const selectedWorkstationId = watch("selectedWorkstationId");
  const selectedGridId = watch("selectedGridId");
  const receiverUserId = watch("receiverUserId");

  const portsForSelectedWs = useMemo(() => {
    const ws = workstationsMap[selectedWorkstationId];
    if (!ws) return [];

    const ports = [...ws.ports];

    const parentPortId = ws.ports.find((p) => p.parentPortId)?.parentPortId;

    const shouldAddParent =
      parentPortId && !ports.some((p) => p.portId === parentPortId);

    const parentPort: WorkstationPortDto | null = shouldAddParent
      ? {
          portId: parentPortId,
          parentPortId: undefined,
          binSize: 0,
          cells: [],
          cleaningDirection: "No Direction",
          configuration: "single",
          coordinate: { x: 0, y: 0 },
          inductionBinConfiguration: []
        }
      : null;

    return parentPort ? [parentPort, ...ports] : ports;
  }, [selectedWorkstationId, workstationsMap]);

  const isParentPort = useMemo(() => {
    const ws = workstationsMap[selectedWorkstationId];
    if (!ws) return false;

    const selected = portsForSelectedWs.find(
      (p) => p.portId === Number(selectedPortId)
    );

    return (
      selected?.parentPortId === undefined || selected?.parentPortId === null
    );
  }, [
    selectedPortId,
    selectedWorkstationId,
    portsForSelectedWs,
    workstationsMap
  ]);

  const [binStates, setBinStates] = useState<Record<string, "O" | "C">>({
    "123": "C",
    "456": "C",
    "789": "C"
  });

  useEffect(() => {
    const isValid = gridList.some((g) => g.autostoreGridId === selectedGridId);

    if (gridList.length && (selectedGridId === "--" || !isValid)) {
      setValue("selectedGridId", gridList[0].autostoreGridId);
    }
  }, [gridList, selectedGridId, setValue]);

  useEffect(() => {
    const ws = siteWorkstationId
      ? workstationsMap[siteWorkstationId]
      : undefined;

    // hydrate selectedWorkstationId only if it's valid for the current grid
    if (ws && selectedWorkstationId === "--") {
      const needGridUpdate =
        selectedGridId === "--" || selectedGridId !== ws.autostoreGridId;

      if (
        needGridUpdate &&
        gridList.some((g) => g.autostoreGridId === ws.autostoreGridId)
      ) {
        setValue("selectedGridId", ws.autostoreGridId, { shouldDirty: false });
      }

      setValue("selectedWorkstationId", ws.id, { shouldDirty: false });
    } // hydrate receiverUserId based on selected workstation
    const selectedWs = workstationsMap[selectedWorkstationId];
    if (receiverUserId === "--" && selectedWs) {
      setValue("receiverUserId", extractReceipientId(selectedWs.userId));
    }

    // hydrate selectedPortId from first non-parent port
    if (selectedPortId === "--" && selectedWs) {
      const firstChildPort = selectedWs.ports.find(
        (p) => p.parentPortId !== null
      );
      if (firstChildPort) {
        setValue("selectedPortId", firstChildPort.portId.toString());
      }
    }
  }, [
    gridList,
    selectedGridId,
    workstations,
    selectedWorkstationId,
    siteWorkstationId,
    sitePortId,
    selectedPortId,
    receiverUserId,
    workstationsMap,
    setValue
  ]);
  const createPortErrorEvent = () => {
    hubs.gridV2.next({
      case: "PortError",
      event: {
        gridId: selectedGridId,
        portId: parseInt(selectedPortId, 10),
        hasError: true
      }
    });
  };

  const createPortErrorNoErrorEvent = () => {
    hubs.gridV2.next({
      case: "PortError",
      event: {
        gridId: selectedGridId,
        portId: parseInt(selectedPortId, 10),
        hasError: false
      }
    });
  };

  const createGridStatusEvent = (caseCode: SystemMode) => {
    hubs.gridV2.next({
      case: "SystemModeChanged",
      event: { gridId: selectedGridId, systemMode: caseCode }
    });
  };

  const createBinModeEvent = (binMode: "O" | "C") => {
    const { selectedPortId, selectedBinId, selectedGridId } =
      methods.getValues();

    const portId = parseInt(selectedPortId, 10);
    const binId = parseInt(selectedBinId, 10);

    if (Number.isNaN(binId)) return;

    hubs.gridV2.next({
      case: "BinModeChange",
      event: {
        binId,
        binMode,
        previousBinMode: binMode === "O" ? "C" : "O",
        coordinate: { x: 1, y: 2 },
        gridId: selectedGridId,
        portId
      }
    });
  };

  const handleHandEvent = (handRaised: boolean) => {
    const { selectedWorkstationId } = getValues();
    hubs.gridV2.next({
      case: "HandToggled",
      event: {
        workstationId: selectedWorkstationId,
        raiseHand: handRaised,
        gridId: selectedGridId
      }
    });
  };

  const handleMessageEvent = () => {
    if (!siteFulfillmentCenterId) return;
    void dispatch(
      sendUserMessages({
        fcId: siteFulfillmentCenterId,
        recipientIds: [receiverUserId],
        message: "hello to you"
      })
    );
  };

  const simulatePickEvent = () => {
    hubs.pick.next({
      batchId: "batch-guid-123",
      eventType: "PickCreated",
      portId: Number(selectedPortId),
      pick: {
        pickId: "pick-guid-001",
        allUpcs: ["123456789012"],
        assignedToteId: "tote-guid-123",
        autostoreBinPulledBy: "robot-9",
        brandName: "BrandX",
        canceledReason: "",
        completedBy: "jdoe",
        firstName: "Jane",
        fulfilled: false,
        imageFilename: "product.jpg",
        inventory: undefined,
        isSplit: false,
        lastName: "Doe",
        lineItemId: "lineitem-guid",
        name: "Example Product",
        orderId: "order-guid",
        price: 19.99,
        quantity: { value: 2, units: "each" },
        requestedUpc: "123456789012",
        rescheduled: false,
        scannedUpcs: [],
        sku: "SKU123",
        status: "Scheduled",
        unitFormatted: "$19.99",
        unitIsApproximate: false,
        upc: "123456789012",
        variantId: "variant-guid-001"
      },
      pickingState: {
        allPicks: [],
        batchId: "batch-guid-123",
        batchName: "Batch 1",
        binId: 99,
        currentPicks: [],
        isBinOpened: true,
        orderPriorities: [],
        taskGroupId: 1,
        totes: []
      }
    });
  };

  const simulateToteEvent = () => {
    hubs.tote.next({
      eventType: "On Workstation",
      userId: "user-guid-123",
      gridId: selectedGridId,
      portId: Number(selectedPortId),
      toteIds: ["tote-guid-001"],
      tote: {
        toteId: "tote-guid-001",
        orderId: "order-guid-123",
        status: "Staged",
        temperatureZone: "Ambient",
        size: "Medium",
        toteNumber: 1,
        canceledReason: ""
      }
    });
  };

  const simulatePTLEvent = () => {
    hubs.ptl.next({
      eventType: "StateChange",
      deviceId: "PTL-123",
      gridId: selectedGridId,
      data: {
        adjustable: false,
        flash: true,
        text: "Pick Confirmed",
        toteId: "tote-guid-001"
      }
    });
  };

  const gridOptions = gridList.map((g) => g.autostoreGridId);
  const wsOptions =
    (workstations
      ?.filter((ws) => ws.autostoreGridId === selectedGridId)
      .map((ws) => ws.id) as string[]) || [];

  const gridSelector = (
    <Box
      sx={{
        width: "95%",
        marginTop: "20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end"
      }}
    >
      <InputLabel sx={{ fontSize: "12px" }} id="grid-select-label">
        Grid
      </InputLabel>

      <Select
        id="grid-select"
        value={safeSelectValue(selectedGridId, gridOptions)}
        labelId="grid-select-label"
        onChange={(e: SelectChangeEvent) =>
          setValue("selectedGridId", e.target.value)
        }
        variant="standard"
        sx={{ width: "80%", marginLeft: "15px" }}
      >
        <MenuItem key="--" value="--">
          --
        </MenuItem>
        {gridList.map((g) => (
          <MenuItem key={g.autostoreGridId} value={g.autostoreGridId}>
            {g.autostoreGridName}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );

  const workstationSelector = (
    <Box
      sx={{
        width: "95%",
        marginTop: "20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end"
      }}
    >
      <InputLabel sx={{ fontSize: "12px" }} id="workstation-select-label">
        Workstation
      </InputLabel>
      <Select
        id="workstation-select"
        value={safeSelectValue(selectedWorkstationId, wsOptions)}
        labelId="workstation-select-label"
        onChange={(e: SelectChangeEvent) =>
          setValue("selectedWorkstationId", e.target.value)
        }
        variant="standard"
        sx={{ width: "70%", marginLeft: "15px" }}
      >
        <MenuItem key="--" value="--">
          --
        </MenuItem>
        {workstations
          ?.filter((ws) => ws.autostoreGridId === selectedGridId)
          .map((ws) => (
            <MenuItem key={ws.id} value={ws.id}>
              {ws.deviceId}
            </MenuItem>
          ))}{" "}
      </Select>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none"
        }}
      >
        <Draggable nodeRef={draggableRef}>
          <Box
            ref={draggableRef}
            sx={{
              pointerEvents: "auto",
              backgroundColor: "gray.light",
              position: "absolute",
              border: "1px solid ##777777",
              padding: 5,
              borderRadius: 7,
              top: "25%",
              left: 15,
              display: "flex",
              flexDirection: "column",
              zIndex: 13000,
              boxShadow:
                "5px 5px 15px 5px #FF8080, -9px 5px 15px 5px #FFE488, -7px -5px 15px 5px #8CFF85, 12px -5px 15px 5px #80C7FF, 12px 10px 15px 7px #E488FF, -10px 10px 15px 7px #FF616B, -10px -7px 27px 1px #8E5CFF, 5px 5px 15px 5px rgba(0,0,0,0)",
              overflowY: "scroll",
              overflowX: "hidden",
              maxHeight: "90vh",
              width: "800px"
            }}
          >
            {/* Component Title */}
            <Box
              sx={{
                height: "40px",
                backgroundColor: "darkGray.dark",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                border: "3px solid black",
                boxSizing: "border-box",
                boxShadow: "2px 2px"
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  letterSpacing: "2px",
                  fontFamily: "monospace",
                  color: "#c898e0",
                  cursor: "grab"
                }}
              >
                {t("event simulator").toLocaleUpperCase()}
              </Typography>
            </Box>

            {/* Grid and Workstation Selectors */}
            <Stack
              direction="row"
              spacing={3}
              sx={{ width: "100%", px: 3, pt: 2 }}
            >
              <Box sx={{ flex: 1 }}>{gridSelector}</Box>
              <Box sx={{ flex: 1 }}>{workstationSelector}</Box>
            </Stack>

            {/* sections body */}
            <Stack direction="row" spacing={3} mt={2}>
              {/* Left Column */}
              <Stack spacing={3} flex={1}>
                {/* Grid Events Section */}
                <Stack spacing={1}>
                  <Typography
                    variant="overline"
                    sx={{
                      backgroundColor: "darkGray.light",
                      color: "#9437c3",
                      pl: 2
                    }}
                  >
                    Grid System Status
                  </Typography>
                  {/* Grid System STOPPED Button */}
                  <Box sx={{ width: "100%", display: "flex", mt: 2 }}>
                    <Button
                      fullWidth
                      disabled={selectedGridId === "--"}
                      onClick={() => {
                        createGridStatusEvent("STP");
                      }}
                      color="primary"
                    >
                      STOPPED
                    </Button>
                  </Box>
                  {/* Grid System SERVICE Button */}
                  <Box sx={{ width: "100%", display: "flex", mt: 1.5 }}>
                    <Button
                      fullWidth
                      disabled={selectedGridId === "--"}
                      onClick={() => {
                        createGridStatusEvent("SVC");
                      }}
                      color="primary"
                    >
                      SERVICE
                    </Button>
                  </Box>
                  {/* Grid System RUNNING Button */}
                  <Box sx={{ width: "100%", display: "flex", mt: 1.5 }}>
                    <Button
                      fullWidth
                      disabled={selectedGridId === "--"}
                      onClick={() => {
                        createGridStatusEvent("RUN");
                      }}
                      color="primary"
                    >
                      RUNNING
                    </Button>
                  </Box>{" "}
                </Stack>
                {/* Send Message Section */}
                <Stack spacing={1}>
                  <Typography
                    variant="overline"
                    sx={{
                      backgroundColor: "darkGray.light",
                      color: "#9437c3",
                      pl: 2
                    }}
                  >
                    Message
                  </Typography>
                  <Box
                    sx={{
                      width: "95%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end"
                    }}
                  >
                    <InputLabel
                      sx={{ fontSize: "12px" }}
                      id="userId-select-label"
                    >
                      Recepient User ID
                    </InputLabel>
                    <Select
                      id="userId-select"
                      value={receiverUserId}
                      labelId="userId-select-label"
                      onChange={(e: SelectChangeEvent) =>
                        setValue("receiverUserId", e.target.value)
                      }
                      variant="standard"
                      sx={{ width: "70%", marginLeft: "15px" }}
                    >
                      <MenuItem key="--" value="--">
                        --
                      </MenuItem>
                      {(workstations ?? [])
                        .filter((ws) => ws.status === "Active")
                        .sort((a, b) => a.userId.localeCompare(b.userId))
                        .map((ws) => (
                          <MenuItem
                            key={extractReceipientId(ws.userId)}
                            value={extractReceipientId(ws.userId)}
                          >
                            {extractDisplayName(ws.userId)}
                          </MenuItem>
                        ))}
                    </Select>
                  </Box>
                  {/* Message Button */}
                  <Box sx={{ width: "100%", mt: 2 }}>
                    <Button
                      disabled={receiverUserId === "--"}
                      onClick={() => {
                        handleMessageEvent();
                      }}
                      color="primary"
                    >
                      {t("send message")}
                    </Button>
                  </Box>
                </Stack>

                <Stack spacing={1} mt={3}>
                  <Typography
                    variant="overline"
                    sx={{
                      backgroundColor: "darkGray.light",
                      color: "#9437c3",
                      pl: 2
                    }}
                  >
                    Other Events
                  </Typography>

                  <Button
                    fullWidth
                    disabled={selectedPortId === "--"}
                    onClick={simulatePickEvent}
                    color="primary"
                  >
                    simulate pick event
                  </Button>

                  <Button
                    fullWidth
                    disabled={selectedPortId === "--"}
                    onClick={simulateToteEvent}
                    color="primary"
                  >
                    simulate tote event
                  </Button>

                  <Button
                    fullWidth
                    disabled={selectedGridId === "--"}
                    onClick={simulatePTLEvent}
                    color="primary"
                  >
                    simulate PTL event
                  </Button>
                </Stack>
              </Stack>

              {/* Right Column */}
              <Stack spacing={3} flex={1}>
                {/* Hand Events Section */}
                <Stack spacing={1}>
                  <Typography
                    variant="overline"
                    sx={{
                      backgroundColor: "darkGray.light",
                      color: "#9437c3",
                      pl: 2
                    }}
                  >
                    Hand Raise
                  </Typography>

                  {/* Event Raise Hand Button */}
                  <Box sx={{ width: "100%", mt: 2 }}>
                    <Button
                      fullWidth
                      disabled={selectedWorkstationId === "--"}
                      onClick={() => {
                        handleHandEvent(true);
                      }}
                      color="primary"
                    >
                      raise hand
                    </Button>
                  </Box>
                  {/* Event Lower Hand Button */}
                  <Box sx={{ width: "100%", mt: 1.5 }}>
                    <Button
                      fullWidth
                      disabled={selectedWorkstationId === "--"}
                      onClick={() => {
                        handleHandEvent(false);
                      }}
                      color="primary"
                    >
                      lower hand
                    </Button>
                  </Box>
                </Stack>
                {/* port events section */}
                <Stack spacing={1}>
                  <Typography
                    variant="overline"
                    sx={{ bgcolor: "darkGray.light", color: "#9437c3", pl: 2 }}
                  >
                    Port Events
                  </Typography>

                  {/* Valid Ports Display */}
                  <Box sx={{ width: "95%" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Valid Ports:
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.5,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1
                      }}
                    >
                      {portsForSelectedWs.map((port) => (
                        <Box
                          key={port.portId}
                          component="button"
                          type="button"
                          onClick={() =>
                            setValue("selectedPortId", port.portId.toString())
                          }
                          sx={{
                            backgroundColor:
                              selectedPortId === String(port.portId)
                                ? "#d1eaff"
                                : "#eee",
                            border: "1px solid #aaa",
                            borderRadius: 1,
                            px: 1.5,
                            py: 0.5,
                            fontFamily: "monospace",
                            fontSize: "0.75rem",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "#c2e0ff"
                            }
                          }}
                        >
                          {port.portId}
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* Port Input */}
                  <Box
                    sx={{
                      width: "95%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end"
                    }}
                  >
                    <InputLabel sx={{ fontSize: 12 }} id="port-select-label">
                      Port
                    </InputLabel>
                    <TextField
                      aria-labelledby="port-select-label"
                      variant="standard"
                      sx={{ ml: 2, width: "50%" }}
                      value={selectedPortId === "--" ? "--" : selectedPortId}
                      onChange={(e) =>
                        setValue("selectedPortId", e.target.value)
                      }
                    />
                  </Box>

                  <Button
                    fullWidth
                    disabled={selectedPortId === "--"}
                    onClick={createPortErrorEvent}
                  >
                    port error
                  </Button>
                  <Button
                    fullWidth
                    disabled={selectedPortId === "--"}
                    onClick={createPortErrorNoErrorEvent}
                  >
                    port error no error
                  </Button>
                  <Button
                    fullWidth
                    disabled={selectedPortId === "--"}
                    onClick={() => {
                      hubs.gridV2.next({
                        case: "PortMode",
                        event: {
                          gridId: selectedGridId,
                          portId: parseInt(selectedPortId, 10),
                          portMode: "Open"
                        }
                      });
                    }}
                  >
                    simulate port open
                  </Button>

                  <Button
                    fullWidth
                    disabled={selectedPortId === "--"}
                    onClick={() => {
                      hubs.gridV2.next({
                        case: "PortMode",
                        event: {
                          gridId: selectedGridId,
                          portId: parseInt(selectedPortId, 10),
                          portMode: "Closed"
                        }
                      });
                    }}
                  >
                    simulate port close
                  </Button>

                  <Box
                    sx={{
                      width: "95%",
                      mt: 2
                    }}
                  >
                    <InputLabel sx={{ fontSize: 12 }}>
                      Trigger Bin Events
                    </InputLabel>
                    <Stack direction="row" spacing={1} mt={1}>
                      {Object.entries(binStates).map(([id, mode]) => (
                        <Button
                          key={id}
                          color={mode === "O" ? "primary" : "secondary"}
                          size="small"
                          disabled={isParentPort}
                          onClick={() => {
                            const nextMode = mode === "O" ? "C" : "O";
                            setBinStates((prev) => ({
                              ...prev,
                              [id]: nextMode
                            }));
                            setValue("selectedBinId", id);
                            createBinModeEvent(nextMode);
                          }}
                          sx={{ minWidth: 50 }}
                        >
                          {id}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Draggable>
      </Box>
    </>
  );
}

export default EventSimulator;
